import {
  ZDrawTypeFullResult,
  ZDrawTypeNumberSetup,
  ZEntryLine,
  ZSimulatedWinners,
} from '@daymade/common-lib/dbJsonModels';
import { z } from 'zod';

import {
  zodSelectDraw,
  zodSelectDrawSchedule,
  zodSelectDrawType,
  zodSelectDrawTypePlan,
  zodSelectDrawTypePlanPrizeTier,
} from '../../generated/zodSchemas.generated';
import { ZPrizeSelect } from '../../prize';

export const ZDrawTypeSelect = zodSelectDrawType
  .pick({
    code: true,
    name: true,
    gameType: true,
    group: true,
  })
  .extend({
    numberSetup: ZDrawTypeNumberSetup.nullable(),
  });

export type TDrawTypeSelect = z.infer<typeof ZDrawTypeSelect>;

export const ZDrawTypePlanSelect = zodSelectDrawTypePlan
  .pick({
    id: true,
    drawTypeCode: true,
    code: true,
    subcode: true,
    name: true,
    desc: true,
    priceVersion: true,
  })
  .extend({
    priceTable: z.record(
      z.string(),
      z.record(z.string(), z.record(z.string(), z.number())),
    ),
  });
export type TDrawTypePlanSelect = z.infer<typeof ZDrawTypePlanSelect>;

export const ZDrawScheduleSelect = zodSelectDrawSchedule.pick({
  id: true,
  templateName: true,
  description: true,
  code: true,
  drawTypeCode: true,
  prizeStructureId: true,
});
export type TDrawScheduleSelect = z.infer<typeof ZDrawScheduleSelect>;

export const ZDrawScheduleSelectAdmin = zodSelectDrawSchedule.pick({
  id: true,
  templateName: true,
  description: true,
  code: true,
  drawTypeCode: true,
  prizeStructureId: true,
  verificationScheme: true,
  autoRun: true,
});

export const ZDrawSelect = zodSelectDraw
  .pick({
    id: true,
    name: true,
    description: true,
    status: true,
  })
  .extend({
    drawDate: z.coerce.date().nullable(),
    pendingResult: z
      .optional(z.record(z.string(), ZDrawTypeFullResult))
      .nullable(),
    result: z.optional(ZDrawTypeFullResult).nullable(),
  });
export type TDrawSelect = z.infer<typeof ZDrawSelect>;

export const ZDrawSelectAdmin = zodSelectDraw
  .pick({
    id: true,
    name: true,
    description: true,
    status: true,
    pendingResult: true,
    verified: true,
    winnersNotified: true,
    participantsNotified: true,
    prizesAwardedAt: true,
  })
  .extend({
    drawDate: z.coerce.date().nullable(),
    pendingResult: z
      .optional(z.record(z.string(), ZDrawTypeFullResult))
      .nullable(),
    result: z.optional(ZDrawTypeFullResult).nullable(),
    simulatedWinners: z.optional(ZSimulatedWinners).nullable(),
    prizesAwardedAt: z.coerce.date().nullable(),
  });
export type TDrawSelectAdmin = z.infer<typeof ZDrawSelect>;

export const ZDrawResult = z.object({
  draw: ZDrawSelect,
  drawSchedule: ZDrawScheduleSelect,
});
export type TDrawResult = z.infer<typeof ZDrawResult>;

export const ZDrawAndPrizeResult = z.object({
  draw: ZDrawSelect.pick({
    drawDate: true,
    result: true,
  }),
  drawSchedule: ZDrawScheduleSelect.pick({
    code: true,
  }),
  drawType: ZDrawTypeSelect.pick({
    code: true,
    gameType: true,
  }),
  prize: ZPrizeSelect.pick({
    title: true,
    imageLocations: true,
  }),
});
export type TDrawAndPrizeResult = z.infer<typeof ZDrawAndPrizeResult>;

export const ZGetDrawResults = z.object({
  drawResults: z.array(ZDrawResult),
  drawInProgress: z.boolean(),
});
export type TGetDrawResults = z.infer<typeof ZGetDrawResults>;

export const ZDrawEntry = z.object({
  id: z.string(),
  numbers: ZEntryLine,
  drawResult: ZDrawTypeFullResult.nullable(),
  drawTypeCode: z.string(),
  drawTypePlanCode: z.string(),
  prizeTierCode: z.string().nullable().optional(),
  prizeTierId: z.number().nullable().optional(),
  prizeStructureId: z.number().nullable().optional(),
  prizeChoice: z.number().nullable().optional(),
  prizeWinStatus: z.string().nullable().optional(),
  prizeValue: z.number().nullable().optional(),
  isHypotheticalPrize: z.boolean().optional(),
});

export type TDrawEntry = z.infer<typeof ZDrawEntry>;

export type TDrawTypePlanPrizeTier = z.infer<
  typeof zodSelectDrawTypePlanPrizeTier
>;

export const ZLeaderboardDraw = ZDrawSelect.extend({
  // Add/override fields as needed for leaderboard draws
});
export type TLeaderboardDraw = z.infer<typeof ZLeaderboardDraw>;
